import { Loading } from "../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  KB_VISIBILITIES,
  KB_VISIBILITY_LABELS,
  PUBLISH_STATUS_LABELS,
  renderHelpMarkdown,
  slugify,
  type KbArticleAdmin,
  type KbArticleBody,
  type KbCategoryDto,
  type PublishStatus,
} from "@klantpunt/shared";
import { IconChevronLeft, IconPlus, IconTrash } from "../app/icons";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import {
  kbAdminKey,
  kbArticleKey,
  kbCategoriesKey,
  useKbArticle,
  useKbArticles,
  useKbCategories,
} from "../features/helpcenter/api";
import { errMsg, fmtDateTime, postForm } from "../features/support/api";
import "../features/helpcenter/helpcenter.css";
import "../features/support/support.css";
import { SupportGuard } from "./SupportInbox";

/** Helpcenter in platformbeheer: artikelen van de kennisbank en de categorieën (docs/HELPCENTER.md §5). */
export function HelpcenterAdminPage() {
  return (
    <SupportGuard>
      <ArticleList />
    </SupportGuard>
  );
}

export function HelpcenterArticlePage() {
  return (
    <SupportGuard>
      <ArticleEditor />
    </SupportGuard>
  );
}

type Filter = "all" | PublishStatus;

function ArticleList() {
  const articles = useKbArticles();
  const cats = useKbCategories();
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [editCats, setEditCats] = useState(false);
  const rows = (articles.data ?? []).filter(
    (a) =>
      (filter === "all" || a.status === filter) &&
      (!q.trim() || a.title.toLowerCase().includes(q.trim().toLowerCase())),
  );

  return (
    <div className="page">
      <p className="label">
        <Link to="/platform">Platformbeheer</Link> · <Link to="/platform/support">Support</Link>
      </p>
      <div className="titlerow">
        <h1 className="dot">Helpcenter</h1>
        <div className="btnrow">
          <Link to="/platform/helpcenter/updates" className="btn">
            Updates
          </Link>
          <button type="button" className="btn" onClick={() => setEditCats((v) => !v)} aria-expanded={editCats}>
            Categorieën
          </button>
          <Link to="/platform/helpcenter/artikel/nieuw" className="btn primary">
            <IconPlus size={20} aria-hidden="true" />
            Nieuw artikel
          </Link>
        </div>
      </div>
      {editCats && cats.data && <CategoryEditor categories={cats.data} />}
      <div className="viewtabs sp-filters" role="group" aria-label="Filter">
        {(["all", "draft", "published"] as const).map((f) => (
          <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {f === "all" ? "Alle" : PUBLISH_STATUS_LABELS[f]}
          </button>
        ))}
      </div>
      <input
        type="search"
        className="sp-search"
        placeholder="Zoek op titel"
        aria-label="Zoek een artikel"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {articles.isPending && <Loading />}
      {articles.isSuccess && rows.length === 0 && <p className="muted">Geen artikelen.</p>}
      {(cats.data ?? []).map((c) => {
        const list = rows.filter((a) => a.categoryId === c.id);
        if (!list.length) return null;
        return (
          <section key={c.id} className="panel" aria-label={c.title}>
            <h2>{c.title}</h2>
            <ul className="kb-admin-rows">
              {list.map((a) => (
                <li key={a.id}>
                  <Link to={`/platform/helpcenter/artikel/${a.id}`} className="kb-admin-row">
                    <span className="grow">{a.title}</span>
                    <span className={`chip${a.status === "published" ? " sp-st-done" : ""}`}>
                      {PUBLISH_STATUS_LABELS[a.status]}
                    </span>
                    <span className="muted small">{KB_VISIBILITY_LABELS[a.visibility]}</span>
                    <span className="muted small" title="Was dit artikel nuttig?">
                      {a.helpfulYes} ja · {a.helpfulNo} nee
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function CategoryEditor({ categories }: { categories: KbCategoryDto[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState(categories);
  const [busy, setBusy] = useState(false);
  useEffect(() => setRows(categories), [categories]);

  async function save(c: KbCategoryDto | Omit<KbCategoryDto, "id">) {
    setBusy(true);
    try {
      const body = { slug: c.slug, title: c.title, icon: c.icon, sort: c.sort };
      if ("id" in c && c.id) await api(`/helpcenter/categories/${c.id}`, { method: "PUT", body });
      else await api("/helpcenter/categories", { method: "POST", body });
      await qc.invalidateQueries({ queryKey: kbCategoriesKey });
      toast("Categorie opgeslagen");
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: string) {
    try {
      await api(`/helpcenter/categories/${id}`, { method: "DELETE" });
      await qc.invalidateQueries({ queryKey: kbCategoriesKey });
    } catch (e) {
      toast(errMsg(e));
    }
  }
  const set = (i: number, patch: Partial<KbCategoryDto>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <section className="panel" aria-labelledby="cat-h">
      <h2 id="cat-h">Categorieën</h2>
      <p className="muted small">
        Icoon: de naam van een Lucide-icoon, bijvoorbeeld <code>rocket</code> of <code>map-pin</code>.
      </p>
      {rows.map((c, i) => (
        <div key={c.id || `new-${i}`} className="kb-cat-row">
          <label className="field">
            <span>Titel</span>
            <input value={c.title} onChange={(e) => set(i, { title: e.target.value })} />
          </label>
          <label className="field">
            <span>Slug</span>
            <input value={c.slug} onChange={(e) => set(i, { slug: e.target.value })} />
          </label>
          <label className="field">
            <span>Icoon</span>
            <input value={c.icon} onChange={(e) => set(i, { icon: e.target.value })} />
          </label>
          <label className="field">
            <span>Volgorde</span>
            <input type="number" min={0} value={c.sort} onChange={(e) => set(i, { sort: Number(e.target.value) })} />
          </label>
          <div className="btnrow">
            <button type="button" className="btn small" disabled={busy} onClick={() => void save(c)}>
              Opslaan
            </button>
            {c.id && (
              <button
                type="button"
                className="btn small icon"
                aria-label={`${c.title} verwijderen`}
                onClick={() => void remove(c.id)}
              >
                <IconTrash size={18} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn small"
        onClick={() => setRows([...rows, { id: "", slug: "", title: "", icon: "book-open", sort: rows.length + 1 }])}
      >
        <IconPlus size={18} aria-hidden="true" />
        Categorie toevoegen
      </button>
    </section>
  );
}

const emptyArticle = (categoryId: string): KbArticleBody => ({
  categoryId,
  slug: "",
  title: "",
  bodyMd: "",
  visibility: "public",
  status: "draft",
  sort: 0,
});

function ArticleEditor() {
  const { id } = useParams();
  const isNew = id === "nieuw";
  const q = useKbArticle(isNew ? undefined : id);
  const cats = useKbCategories();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState<KbArticleBody | null>(null);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const text = useRef<HTMLTextAreaElement>(null);
  const loaded: KbArticleAdmin | undefined = q.data;

  useEffect(() => {
    if (form) return;
    if (isNew && cats.data?.[0]) setForm(emptyArticle(cats.data[0].id));
    if (loaded)
      setForm({
        categoryId: loaded.categoryId,
        slug: loaded.slug,
        title: loaded.title,
        bodyMd: loaded.bodyMd,
        visibility: loaded.visibility,
        status: loaded.status,
        sort: loaded.sort,
      });
  }, [isNew, cats.data, loaded, form]);

  const preview = useMemo(() => renderHelpMarkdown(form?.bodyMd ?? ""), [form?.bodyMd]);
  if (!form)
    return (
      <p className="page muted">
        {isNew && cats.isSuccess && !cats.data.length ? "Maak eerst een categorie aan onder Helpcenter." : "Laden…"}
      </p>
    );

  const set = (patch: Partial<KbArticleBody>) => setForm({ ...form, ...patch });

  function insert(snippet: string) {
    const el = text.current;
    const at = el ? el.selectionStart : form!.bodyMd.length;
    const before = form!.bodyMd.slice(0, at);
    const sep = before && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : "";
    set({ bodyMd: `${before}${sep}${snippet}\n${form!.bodyMd.slice(at)}` });
  }

  async function upload(file: File) {
    const fd = new FormData();
    fd.set("file", file);
    try {
      const r = await postForm<{ markdown: string }>("/helpcenter/images", fd);
      insert(r.markdown);
    } catch (e) {
      toast(errMsg(e));
    }
  }

  async function save(e: FormEvent | null, status: PublishStatus) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { ...form!, status };
      const d = isNew
        ? await api<KbArticleAdmin>("/helpcenter/articles", { method: "POST", body })
        : await api<KbArticleAdmin>(`/helpcenter/articles/${id}`, { method: "PUT", body });
      qc.setQueryData(kbArticleKey(d.id), d);
      void qc.invalidateQueries({ queryKey: kbAdminKey });
      setForm({ ...form!, status });
      toast(status === "published" ? "Gepubliceerd" : "Opgeslagen als concept");
      if (isNew) navigate(`/platform/helpcenter/artikel/${d.id}`, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Dit artikel verwijderen?")) return;
    await api(`/helpcenter/articles/${id}`, { method: "DELETE" });
    void qc.invalidateQueries({ queryKey: kbAdminKey });
    navigate("/platform/helpcenter", { replace: true });
  }

  return (
    <div className="page">
      <Link to="/platform/helpcenter" className="backlink">
        <IconChevronLeft size={18} aria-hidden="true" />
        Helpcenter
      </Link>
      <div className="titlerow">
        <h1 className="dot">{isNew ? "Nieuw artikel" : form.title || "Artikel"}</h1>
        <span className={`chip${form.status === "published" ? " sp-st-done" : ""}`}>
          {PUBLISH_STATUS_LABELS[form.status]}
        </span>
      </div>
      <form className="panel" onSubmit={(e) => void save(e, form.status)}>
        <div className="kb-fields">
          <label className="field">
            <span>Titel</span>
            <input
              required
              maxLength={160}
              value={form.title}
              onChange={(e) =>
                set({ title: e.target.value, ...(slugTouched ? {} : { slug: slugify(e.target.value) }) })
              }
            />
          </label>
          <label className="field">
            <span>Slug (adres)</span>
            <input
              required
              maxLength={80}
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                set({ slug: e.target.value });
              }}
            />
          </label>
          <label className="field">
            <span>Categorie</span>
            <select value={form.categoryId} onChange={(e) => set({ categoryId: e.target.value })}>
              {(cats.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Zichtbaarheid</span>
            <select
              value={form.visibility}
              onChange={(e) => set({ visibility: e.target.value as KbArticleBody["visibility"] })}
            >
              {KB_VISIBILITIES.map((v) => (
                <option key={v} value={v}>
                  {KB_VISIBILITY_LABELS[v]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Volgorde</span>
            <input type="number" min={0} value={form.sort} onChange={(e) => set({ sort: Number(e.target.value) })} />
          </label>
        </div>
        <div className="btnrow">
          <button type="button" className="btn small" onClick={() => insert(":::tip\nTekst van de tip\n:::")}>
            Tip-blok
          </button>
          <label className="btn small">
            Afbeelding invoegen
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
                e.target.value = "";
              }}
            />
          </label>
          <span className="muted small">Markdown: ## kop, **vet**, *cursief*, - lijst, [tekst](link)</span>
        </div>
        <div className="kb-editor">
          <label className="field">
            <span>Inhoud</span>
            <textarea ref={text} value={form.bodyMd} onChange={(e) => set({ bodyMd: e.target.value })} />
          </label>
          <div className="field">
            <span>Voorbeeld</span>
            <div
              className="kb-article kb-preview"
              data-testid="kb-preview"
              dangerouslySetInnerHTML={{ __html: preview }}
            />
          </div>
        </div>
        {error && (
          <p className="warn" role="alert">
            {error}
          </p>
        )}
        <div className="btnrow">
          <button type="button" className="btn" disabled={busy} onClick={() => void save(null, "draft")}>
            {form.status === "published" ? "Terug naar concept" : "Opslaan als concept"}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void save(null, "published")}>
            {form.status === "published" ? "Wijzigingen publiceren" : "Publiceren"}
          </button>
          {!isNew && (
            <button type="button" className="btn danger" onClick={() => void remove()}>
              Verwijderen
            </button>
          )}
        </div>
      </form>
      {loaded && (
        <section className="panel" aria-labelledby="fb-h">
          <h2 id="fb-h">Was dit artikel nuttig?</h2>
          <p>
            {loaded.helpfulYes} keer ja, {loaded.helpfulNo} keer nee.
          </p>
          {loaded.comments.length > 0 && (
            <ul className="kb-hits">
              {loaded.comments.map((c, i) => (
                <li key={i}>
                  <span className="muted small">{fmtDateTime(c.createdAt)}</span>
                  <p>{c.comment}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
